'use client';

import { ReactNode, useEffect, useMemo } from 'react';

import { classNames } from '@bytel/product-wall/helpers';
import {
    Alignable,
    Box,
    BoxContent,
    BoxHeader,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Divider,
    IconName,
    Justifiable,
    Price,
    PriceLevel,
    Row,
    Rows,
    Sticker,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TypographyAlign,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { DiscountType } from '@app-types/discount';
import { FaiType } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { BigIcon, BigIconVariant } from '@components/big';
import { InlinePrice } from '@components/inline-price';

import { useMarketings } from '@hooks/use-marketings';

import { calculateFaiForeverPrice, calculateFaiPrice } from '@helpers/fai';
import { MarketingsEventState } from '@helpers/marketings';
import { capitalize } from '@helpers/wording';

import { MarketingType } from '@constants/plan';
import { CUSTOM_EVENTS } from '@constants/provider';

import { getPromoDuration } from '@promo-simulator/helpers';
import { OnClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

import { NuggetsList } from './nuggets-list';
import { SpeedOption } from './speed-option';

interface CtaType {
    color?: ButtonVariant;
    text: string;
    onClick?: (event: OnClickEvent | undefined) => void;
    disabled?: boolean;
    alternativeState?: ReactNode;
    href?: string;
}

interface RocCardProps {
    plan?: FaiType;
    cta?: CtaType;
    shadowless?: boolean;
    withBorder?: boolean;
    hasFaiProductWithFourNuggets?: boolean;
    smallSticker?: boolean;
    user?: UserType;
    hasSiblingHat?: boolean;
}

export function RocCard({
    plan,
    cta,
    shadowless = false,
    withBorder = false,
    hasFaiProductWithFourNuggets,
    smallSticker = true,
    user,
    hasSiblingHat = false,
}: Readonly<RocCardProps>) {
    const [marketingsState, setMarketingsState] = useMarketings();

    const { price, priceForever, sticker, hasHomePromo } = useMemo(() => {
        if (!plan) {
            return { price: 0, priceForever: 0, sticker: null };
        }

        const shouldChooseIncitation = marketingsState[MarketingType.MOBILE] && !user;

        const promo = plan.details.discounts.find(
            (discount: DiscountType) => discount.isIncitation === shouldChooseIncitation && discount.pennant,
        );

        const hasHomePromo = promo?.marketingTypes.includes(MarketingType.HOME);

        const sticker = plan.stickers?.[0]?.label ? (
            <Sticker variant={plan.stickers?.[0]?.color} label={plan.stickers[0].label} small={smallSticker} />
        ) : null;

        return {
            price: calculateFaiPrice(plan, shouldChooseIncitation),
            priceForever: calculateFaiForeverPrice(plan, shouldChooseIncitation),
            sticker,
            hasHomePromo,
        };
    }, [plan?.details?.discounts, plan?.details?.price, user, marketingsState]);

    const handlePriceContextChange = (event: Event) => {
        const { detail } = event as CustomEvent<MarketingsEventState>;
        const marketingsDetails = {
            [MarketingType.MOBILE]: detail.mobile,
        };
        setMarketingsState(marketingsDetails);
    };

    useEffect(() => {
        window.addEventListener(CUSTOM_EVENTS.DISCOUNT_CONFIGURATOR, handlePriceContextChange);

        return () => {
            window.removeEventListener(CUSTOM_EVENTS.DISCOUNT_CONFIGURATOR, handlePriceContextChange);
        };
    }, []);

    if (!plan) {
        return null;
    }

    return (
        <Box
            shadowless={shadowless}
            active={withBorder}
            flat={withBorder}
            className={classNames('is-fullheight', plan.hat ? 'has-outline-secondary' : '')}
            headerOffset={hasSiblingHat && !plan.hat}
        >
            {plan.hat && (
                <BoxHeader className='is-justified-center'>
                    <Text level={TextLevels.ONE} marginless typo={TypographyAlign.TEXT_CENTERED}>
                        {plan.hat}
                    </Text>
                </BoxHeader>
            )}
            <BoxContent className={shadowless ? 'is-paddingless' : ''}>
                <View flexable fullwidth className='is-flex-col gap-medium has-text-centered is-fullheight'>
                    <Columns mobile marginless className='roc-title'>
                        <Column className='has-text-left'>
                            <Title level={TitleLevels.FOUR}>{plan.name}</Title>
                            {plan.subtitle && (
                                <Text marginless level={TextLevels.ONE}>
                                    {plan.subtitle}
                                </Text>
                            )}
                        </Column>
                        <Column verticalAlign={Alignable.ALIGNED_CENTER} narrow>
                            <img
                                alt={`Image de l'offre ${plan.name}`}
                                aria-hidden='true'
                                className='roc-image'
                                src={plan.image}
                            />
                        </Column>
                    </Columns>

                    <View
                        flexable
                        align={Alignable.ALIGNED_CENTER}
                        justify={Justifiable.JUSTIFIED_CENTER}
                        className='roc-sticker'
                    >
                        {sticker}
                    </View>

                    {price > 0 && (
                        <div className='price-and-big-icon'>
                            <Price
                                level={PriceLevel.FOUR}
                                align={Alignable.ALIGNED_CENTER}
                                amount={price}
                                period='mois'
                            />
                            <View
                                style={{
                                    opacity: hasHomePromo && marketingsState[MarketingType.MOBILE] ? '100%' : '0',
                                    transition: 'ease-in .1s',
                                }}
                                flexable
                                className='big-icon has-custom-big-background'
                            >
                                <BigIcon height={16} variant={BigIconVariant.INFO} />
                            </View>
                        </div>
                    )}

                    <View className='roc-offer-info'>
                        {hasHomePromo && marketingsState[MarketingType.MOBILE] && (
                            <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} level={TextLevels.TWO} marginless>
                                Même après 12 mois grâce à votre forfait
                            </Text>
                        )}
                        {price !== priceForever && (
                            <Text level={TextLevels.TWO} marginless>
                                Pendant {getPromoDuration(plan.details?.discounts)} mois puis{' '}
                                <InlinePrice amount={priceForever} period='mois' typo={[]} level={TextLevels.TWO} />
                            </Text>
                        )}
                        {plan.obligationLabel && (
                            <Text level={TextLevels.TWO} marginless>
                                {capitalize(plan.obligationLabel)}
                            </Text>
                        )}
                    </View>

                    {cta && (
                        <View
                            flexable
                            justify={Justifiable.JUSTIFIED_CENTER}
                            align={Alignable.ALIGNED_CENTER}
                            className='roc-cta'
                        >
                            <CtaButton cta={cta} />
                        </View>
                    )}

                    {(plan.nuggets?.length ?? 0) > 0 && (
                        <Row narrow className={`roc-nuggets ${hasFaiProductWithFourNuggets ? 'four-nuggets' : ''}`}>
                            <NuggetsList plan={plan} />
                        </Row>
                    )}

                    {plan.speedOption && (
                        <Row narrow>
                            <SpeedOption speedOption={plan.speedOption} />
                        </Row>
                    )}

                    <div className='mt-auto'>
                        {(plan.options?.length ?? 0) > 0 && (
                            <>
                                <Divider iconName={IconName.PLUS} />
                                <Columns mobile className='roc-options'>
                                    {plan.options
                                        ?.sort((a, b) => a.position - b.position)
                                        .slice(0, 3)
                                        .map((option) => {
                                            return (
                                                <Column size={4} key={`$col-${plan.gencode}-${option.label}`}>
                                                    <Rows>
                                                        <Row className='is-paddingless is-flex is-centered'>
                                                            <img
                                                                key={`img-${plan.gencode}-${option.label}`}
                                                                width={66}
                                                                alt={option.logo_alt}
                                                                src={option.logo_url}
                                                            />
                                                        </Row>
                                                        <Row>
                                                            <Text marginless level={TextLevels.FOUR}>
                                                                {option.label}
                                                            </Text>
                                                        </Row>
                                                    </Rows>
                                                </Column>
                                            );
                                        })}
                                </Columns>
                            </>
                        )}

                        {plan.detailsUrl && (
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                fullwidth
                                href={plan.detailsUrl}
                                className='mt-auto'
                            >
                                Voir les détails
                            </Button>
                        )}
                    </div>
                </View>
            </BoxContent>
        </Box>
    );
}

interface CtaButtonProps {
    cta: CtaType;
}

function CtaButton({ cta }: Readonly<CtaButtonProps>) {
    if (cta.alternativeState) {
        return cta.alternativeState;
    }

    return (
        <Button
            fullwidth
            variant={cta.color ?? ButtonVariant.CONVERSION}
            onClick={cta.onClick}
            disabled={cta.disabled}
            href={cta.href}
            markup={cta.href ? 'a' : 'button'}
            className='!is-marginless'
        >
            {cta.text}
        </Button>
    );
}
